import { createDeferred } from '@forest/native-services'
import { createStore } from 'solid-js/store'
import type { CatalogueSortable } from '~/lib/classic-sorting'
import {
  isDeepLinkTarget,
  type DeepLinkTarget,
  type ShopTab,
} from '~/lib/deep-link'
import type { RemoteArtworkStatus } from '~/lib/remote-image'
import type {
  CatalogueCandidate,
  CatalogueContent,
  CatalogueProduct,
} from '~/services/catalogue'

// How many leading Classic slots the Classic landing's reveal surface holds
// (GLOSSARY.md "Reveal surface"): on phones (3 columns) this is six rows —
// about a screen and a half of cards — so every card the boot skeleton gives
// way to is real.
export const FIRST_REVEAL_COUNT = 18

// What the landing tab's reveal surface holds (GLOSSARY.md "Reveal
// surface"): how many leading slots of each grid, in display order, and
// whether the hero cards' banners and the daily-pick unowned count join it.
// One definition for the reveal predicate and the hydration order, so the
// two cannot drift. A top_up landing's surface is the currency catalogue,
// not a grid.
export interface SurfaceWindow {
  classic: number
  dailyPickUnownedCount: boolean
  dailyPicks: number
  heroBanners: boolean
  heroRow: number
}

const NO_GRID: SurfaceWindow = {
  classic: 0,
  dailyPickUnownedCount: false,
  dailyPicks: 0,
  heroBanners: false,
  heroRow: 0,
}

export function surfaceWindow(landing: ShopTab): SurfaceWindow {
  switch (landing) {
    case 'featured':
      // The whole tab: every hero slot and banner, every daily-pick slot,
      // and the unowned count that decides the Daily Picks section's shape.
      return {
        ...NO_GRID,
        dailyPickUnownedCount: true,
        dailyPicks: Infinity,
        heroBanners: true,
        heroRow: Infinity,
      }
    case 'classic':
      return { ...NO_GRID, classic: FIRST_REVEAL_COUNT }
    case 'currency':
      return NO_GRID
  }
}

// The leading run of one content kind in the Classic grid — the kind the
// Classic content filter opens on: the grid orders every tree before every
// land skin (ADR 0033), so the run is the trees when there are any and the
// land skins otherwise, exactly what the filter shows first.
export function leadingKindCount(
  slots: readonly { content: CatalogueContent }[],
): number {
  const first = slots[0]?.content
  let count = 0
  while (count < slots.length && slots[count]!.content === first) count++
  return count
}

// The Classic surface window on a grid (GLOSSARY.md "Reveal surface"): the
// reveal count, capped at the leading kind's run — the slot of the other
// kind is never on screen to wait for. Shared by the gate and the progress
// readers so they cannot drift.
export function classicSurfaceSize(
  slots: readonly { content: CatalogueContent }[],
  window: number,
): number {
  return Math.min(window, leadingKindCount(slots))
}

// The Shop hydration order's gate on one grid (GLOSSARY.md "Shop hydration
// order"): slots whose display index is onSurface hydrate when the gate
// opens — at creation, behind the boot skeleton, unless opened says later
// (the Classic tab's first entry, ADR 0049); every other slot exists as a
// pending skeleton and waits for released — the Shop reveal, or the Classic
// reveal for a Classic grid opened late. A deep-link target ignores the
// gate: it always hydrates first.
export interface HydrationGate {
  // In-view hydration (GLOSSARY.md; ADR 0051): a slot off the surface also
  // waits until it comes within reach, which the grid's reach reports.
  inView?: boolean
  onSurface: (index: number) => boolean
  opened?: Promise<void>
  released: Promise<void>
}

// What a gated slot waits for before it starts; undefined to start at once.
export function gateStart(
  gate: HydrationGate,
  index: number,
): Promise<void> | undefined {
  return gate.onSurface(index) ? gate.opened : gate.released
}

// A fixed grid position, in default display order from creation. Sort keys
// (CatalogueSortable), content identity, and ownership come from the bootstrap
// candidate; hydration only ever fills the slot in place or collapses it —
// slots never reorder on their own.
export interface CatalogueGridSlot extends CatalogueSortable {
  content: CatalogueContent
  owned: boolean
  product?: CatalogueProduct
  status: 'pending' | 'hydrated' | 'omitted'
}

// Whether a grid still has anything to show: a pending slot may yet hydrate,
// a hydrated one is a card. A collection whose every slot is omitted is as
// empty as one with no candidates (GLOSSARY.md "Empty Shop collection").
export function hasVisibleSlots(slots: readonly CatalogueGridSlot[]): boolean {
  return slots.some((slot) => slot.status !== 'omitted')
}

// A slot's effective ownership: the shell's session-unlock set — keyed by
// (content, gid), so tree and land-skin gids never mark each other — over
// the bootstrap candidate's own flag. Every ownership judgment on a card
// grid reads it through here, so a card's Owned tag and the Super Collector
// state can never disagree.
export function slotOwned(
  slot: CatalogueGridSlot,
  isOwned?: (content: CatalogueContent, gid: number) => boolean,
): boolean {
  return isOwned?.(slot.content, slot.gid) ?? slot.owned
}

export interface CatalogueGrid {
  // Resolves once every slot has settled (hydrated or omitted) — the moment
  // a grid hydrated off-screen may swap in (ADR 0028's rollover swap).
  settled: Promise<void>
  slots: CatalogueGridSlot[]
}

export interface HydratedSlots extends CatalogueGrid {
  // Reports that a slot came within reach of the screen; under an in-view
  // gate that lets it start once released. Later calls, and calls on any
  // other gate, no-op.
  reach: (slot: CatalogueGridSlot) => void
}

// The slot machinery shared by every Classic-style card grid (the Classic tab
// and the Featured tab's Daily Picks): one fixed slot per candidate in the
// given order, each filling in place as its own hydration settles. The
// priority candidate (the launch deep link's target) hydrates ahead of every
// other so its detail can open before the reveal surface settles. A gate
// holds the slots outside the reveal surface until the Shop reveal — under an
// in-view gate, also until each comes within reach; without one every slot
// hydrates at creation.
export function createHydratedSlots<Candidate extends CatalogueCandidate>(
  candidates: Candidate[],
  hydrate: (candidate: Candidate) => Promise<CatalogueProduct | null>,
  options?: {
    gate?: HydrationGate
    priority?: (candidate: Candidate) => boolean
  },
): HydratedSlots {
  const [slots, setSlots] = createStore<CatalogueGridSlot[]>(
    candidates.map((candidate) => ({
      content: candidate.content,
      gid: candidate.gid,
      owned: candidate.owned,
      price: candidate.price,
      status: 'pending',
    })),
  )

  const order = candidates.map((_, index) => index)
  const priorityIndex = options?.priority
    ? candidates.findIndex(options.priority)
    : -1
  if (priorityIndex > 0) {
    order.splice(order.indexOf(priorityIndex), 1)
    order.unshift(priorityIndex)
  }

  const gate = options?.gate
  // What lets each slot an in-view gate holds off the surface start, by its
  // grid position. A slot never brought within reach never starts, so such a
  // grid's settled may never resolve.
  const reachers = new Map<number, () => void>()
  const pending = order.map((index) => {
    const run = () =>
      hydrate(candidates[index]!)
        // hydrate is total (failures become omissions); this backstop
        // settles the slot on a programming error so the reveal gate cannot
        // stall on it.
        .catch(() => null)
        .then((product) => {
          if (product) setSlots(index, { product, status: 'hydrated' })
          else setSlots(index, 'status', 'omitted')
        })
    if (!gate || index === priorityIndex) return run()
    if (gate.inView && !gate.onSurface(index)) {
      const reach = createDeferred()
      reachers.set(index, reach.done)
      return Promise.all([gate.released, reach.promise]).then(run)
    }
    const start = gateStart(gate, index)
    return start ? start.then(run) : run()
  })

  return {
    reach: (slot) => reachers.get(slots.indexOf(slot))?.(),
    settled: Promise.all(pending).then(() => undefined),
    slots,
  }
}

// Progress of the leading windowSize slots of one grid on the landing's
// reveal surface — the slots the reveal gate watches.
export function windowProgress(
  slots: readonly Pick<CatalogueGridSlot, 'status'>[],
  windowSize = FIRST_REVEAL_COUNT,
): {
  settled: number
  size: number
} {
  const size = Math.min(windowSize, slots.length)
  let settled = 0
  for (let index = 0; index < size; index++) {
    if (slots[index]!.status !== 'pending') settled++
  }
  return { settled, size }
}

// Progress of the Classic landing's reveal surface on a Classic grid — the
// slots the Classic reveal watches (GLOSSARY.md "Classic reveal").
export function classicSurfaceProgress(slots: readonly CatalogueGridSlot[]): {
  settled: number
  size: number
} {
  return windowProgress(
    slots,
    classicSurfaceSize(slots, surfaceWindow('classic').classic),
  )
}

export interface RevealSurfaceInput {
  currencySettled: boolean
  // Whether the daily-pick unowned count query settled, with data or an
  // error: the Daily Picks section's final shape depends on it.
  dailyPickUnownedCountSettled: boolean
  dailyPickSlots: CatalogueGridSlot[]
  // The banner load status of every hero card in the DOM, the starter
  // pack's included; an omitted hero renders no card. Trusts that every
  // card the row will render is already mounted: the cards mount with the
  // shell in the same update that creates the hero slots, and a slot only
  // settles after its async hydration, so the slots cannot all settle
  // before their cards register.
  heroBanners: readonly RemoteArtworkStatus[]
  // Every hero-row slot in row order, the starter pack's leading when there
  // is one: the gate reads their settle status only.
  heroSlots: readonly Pick<CatalogueGridSlot, 'status'>[]
  landing: ShopTab
  slots: CatalogueGridSlot[]
  // The launch deep link's target, when there is one: its catalogue
  // identity, and whether its detail dialog is presented — assets settled,
  // loaded or failed, images decoded, enter animation finished. The latter
  // is only read while the target's slot is hydrated.
  target?: RevealTarget
}

export interface RevealTarget extends DeepLinkTarget {
  detailPresented: boolean
}

// Progress of the landing tab's surface: on the Featured tab the whole tab —
// every hero slot, every hero card's banner, every daily-pick slot, and the
// daily-pick unowned count; the first Classic slots on the Classic tab; no
// grid slot on a top_up landing (its surface is the currency catalogue).
// Shared by the reveal predicate and the deadline diagnostics snapshot so
// the two cannot drift.
export function landingSurfaceProgress(
  input: Omit<RevealSurfaceInput, 'currencySettled' | 'target'>,
): { settled: number; size: number } {
  const surface = surfaceWindow(input.landing)
  const banners = surface.heroBanners ? input.heroBanners : []
  const parts = [
    windowProgress(
      input.slots,
      classicSurfaceSize(input.slots, surface.classic),
    ),
    windowProgress(input.heroSlots, surface.heroRow),
    windowProgress(input.dailyPickSlots, surface.dailyPicks),
    {
      settled: banners.filter((status) => status !== 'loading').length,
      size: banners.length,
    },
    surface.dailyPickUnownedCount
      ? { settled: input.dailyPickUnownedCountSettled ? 1 : 0, size: 1 }
      : { settled: 0, size: 0 },
  ]
  return {
    settled: parts.reduce((sum, part) => sum + part.settled, 0),
    size: parts.reduce((sum, part) => sum + part.size, 0),
  }
}

// The landing surface's reveal predicate. A hydrated deep-link target
// reveals once its detail is presented — open over the grid, completed, so
// the boot skeleton gives way onto it (GLOSSARY.md "Shop reveal") — while a
// pending target holds the reveal; a target that was omitted or never
// existed degrades to the landing tab's rule, which for a product deep link
// is always the Classic tab's (ADR 0048): the whole tab settled
// (landingSurfaceProgress) on the Featured tab, the Classic surface's slots
// settled on the Classic tab, the currency catalogue settled on the Coins &
// Crystals tab.
export function revealSurfaceReady(input: RevealSurfaceInput): boolean {
  const { currencySettled, landing, slots, target } = input
  if (target) {
    const slot = slots.find((entry) => isDeepLinkTarget(entry, target))
    if (slot?.status === 'pending') return false
    if (slot?.status === 'hydrated') return target.detailPresented
  }
  if (landing === 'currency') return currencySettled
  const { settled, size } = landingSurfaceProgress(input)
  return settled === size
}
